import { ReactNode } from 'react';

import {
  Divider,
  HexIcon,
  Icons,
  LayoutCard,
  Placeholder,
  Typography,
  Stack,
} from '@cast/design-system';

const SUPPORTED_PORTS = '8080, 8443, 8090, 6379';

type Reason = {
  icon: ReactNode;
  title: string;
  description: string;
};

const COLLECTOR_REASON: Reason = {
  icon: <Icons.Terminal size={20} weight="fill" />,
  title: "Collector wasn't running",
  description:
    'The reliability metrics collector was not running during the selected time period.',
};

const CLUSTER_REASONS: Reason[] = [
  COLLECTOR_REASON,
  {
    icon: <Icons.HardDrives size={20} weight="fill" />,
    title: 'No HTTP endpoints',
    description:
      'Workloads may be running background jobs or worker processes that do not expose HTTP endpoints.',
  },
  {
    icon: <Icons.Plugs size={20} weight="fill" />,
    title: 'Unsupported port',
    description: `Applications may be listening on a port that isn't currently monitored. Supported ports: ${SUPPORTED_PORTS}`,
  },
];

const WORKLOAD_REASONS: Reason[] = [
  COLLECTOR_REASON,
  {
    icon: <Icons.HardDrives size={20} weight="fill" />,
    title: 'No HTTP endpoints',
    description:
      'This workload may be running background jobs or worker processes that do not expose HTTP endpoints.',
  },
  {
    icon: <Icons.Plugs size={20} weight="fill" />,
    title: 'Unsupported port',
    description: `The application may be listening on a port that isn't currently monitored. Supported ports: ${SUPPORTED_PORTS}`,
  },
];

type ReasonRowProps = Reason;

const ReasonRow = ({ icon, title, description }: ReasonRowProps) => (
  <Stack direction="row" gap={12}>
    <Stack flexShrink={0} pt={2}>
      {icon}
    </Stack>
    <Stack gap={2}>
      <Typography variant="A1">{title}</Typography>
      <Typography variant="P12R" color="textSecondary">
        {description}
      </Typography>
    </Stack>
  </Stack>
);

type Props = {
  variant: 'cluster' | 'workload';
};

export const ReliabilityNoData = ({ variant }: Props) => {
  const reasons = variant === 'workload' ? WORKLOAD_REASONS : CLUSTER_REASONS;

  return (
    <Stack
      alignItems="center"
      gap={16}
      mt={16}
      data-testid="reliability-no-data"
    >
      <Placeholder
        testId="reliability-no-data-placeholder"
        title="Reliability metrics could not be retrieved"
        body={
          <Stack gap={8} alignItems="center">
            <Typography variant="P14R" color="textSecondary" textAlign="center">
              We couldn&apos;t find any reliability metrics for the selected
              resource during the chosen time period.
            </Typography>
            <Typography variant="P14R" color="textSecondary" textAlign="center">
              It might take up to 5 minutes to see your data after you enable
              reliability metrics.
            </Typography>
            <Typography variant="P14R" color="textSecondary" textAlign="center">
              Possible reasons may include:
            </Typography>
          </Stack>
        }
        icon={<HexIcon size={84} icon={<Icons.Question weight="fill" />} />}
      />
      <LayoutCard sx={{ maxWidth: 480, width: '100%' }}>
        <Stack gap={16} divider={<Divider />}>
          {reasons.map((reason) => (
            <ReasonRow key={reason.title} {...reason} />
          ))}
        </Stack>
      </LayoutCard>
    </Stack>
  );
};
